import "./index.scss";
import {MedicalRecordsEntityResponse} from "../../hooks/api/medicalRecord/interfaces/medicalRecords.response";
import {Box, Typography} from "@mui/material";
import {useEffect, useState} from "react";
import icpLogo from "../../assets/icp_brasil.png";
import QRCode from "react-qr-code";

function ReceiptPDF({record}: { record: MedicalRecordsEntityResponse }) {
    const [examsRequested, setExamsRequested] = useState<string[]>([]);
    const [laboratoryExamsRequested, setLaboratoryExamsRequested] = useState<string[]>([]);
    const [healthCertificate, setHealthCertificate] = useState<string>("");
    const [showCid, setShowCid] = useState<boolean>(false);

    useEffect(() => {
        setExamsRequested(record.examsRequested.split("\n"));
        setLaboratoryExamsRequested(record.laboratoryExamsRequested?.split("\n") || []);
        setHealthCertificate(record.healthCertificate ?? "");
        setShowCid(record.showCid ?? false);
    }, [
        record.examsRequested,
        record.healthCertificate,
        record.laboratoryExamsRequested,
        record.showCid,
    ]);

    //return datetime in format DD/MM/YYYY HH:mm
    const getCurrentDateTime = () => {
        const date = new Date();
        const pad = (n: number) => n.toString().padStart(2, "0");
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${pad(
            date.getFullYear()
        )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    return (
        <div className="pdfBox">
            {/* Page 1 - Medicines */}
            <div className="print-outline-top"></div>
            <div
                className="page-one"
                style={{
                    width: "210mm",
                    height: "297mm",
                    display: "flex",
                    flexDirection: "column",
                    padding: "20mm",
                    boxSizing: "border-box",
                }}
            >
                <div style={{display: "flex", flexDirection: "column"}}>
                    <div className="page-header">
                        <div className="top-table">
                            <div className="top-table-header">
                                <Typography>IDENTIFICAÇÃO DO EMITENTE</Typography>
                            </div>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Nome Completo: ${record.appointment.doctor.user.name}`}
                                </Typography>
                            </Box>
                            <div className="top-table-line">
                                <Typography
                                    sx={{fontSize: 14}}
                                >{`CRM: ${record.appointment.doctor.crm}`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`UF: ${record.appointment.doctor.state}`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`N.º: ${record.appointment.doctor.address.substring(
                                    record.appointment.doctor.address.lastIndexOf(",") + 1
                                )}`}</Typography>
                            </div>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Endereço Completo: ${record.appointment.doctor.address}, ${record.appointment.doctor.neighborhood}`}
                                </Typography>
                            </Box>
                            {record.appointment.doctor.commercialPhone && (
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`Telefone: ${record.appointment.doctor.commercialPhone}`}</Typography>
                            )}
                            <div className="top-table-line">
                                <Typography sx={{fontSize: 14}}>{`Cidade: ${
                                    record.appointment.doctor.city[0].toUpperCase() +
                                    record.appointment.doctor.city.substr(1)
                                }`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`UF: ${record.appointment.doctor.state}`}</Typography>
                            </div>
                        </div>
                        <div className="top-table">
                            <div className="top-table-header">
                                <Typography>IDENTIFICAÇÃO DO PACIENTE</Typography>
                            </div>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Nome Completo: ${record.appointment.patient?.user?.name}`}
                                </Typography>
                            </Box>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Endereço completo: ${record.appointment.patient?.address}, ${record.appointment.patient?.neighborhood}`}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{fontSize: 14, marginLeft: 2}}
                            >{`Telefone: ${record.appointment.patient?.user?.phone}`}</Typography>
                            <div className="top-table-line">
                                <Typography sx={{fontSize: 14}}>{`Cidade: ${
                                    record.appointment.patient?.city[0].toUpperCase() +
                                    record.appointment.patient?.city.substring(1)
                                }`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`UF: ${record.appointment.patient?.state}`}</Typography>
                            </div>
                        </div>
                        <div className="via-indicator">
                            <Typography sx={{fontSize: 10, fontWeight: 'bold', whiteSpace: 'nowrap'}}>1ª VIA
                                FARMÁCIA</Typography>
                            <Typography sx={{fontSize: 10, fontWeight: 'bold', whiteSpace: 'nowrap'}}>2ª VIA
                                PACIENTE</Typography>
                        </div>
                    </div>
                    <Typography
                        style={{
                            fontSize: 20,
                            textAlign: "center",
                            marginTop: 20,
                            fontWeight: "bold",
                        }}
                    >
                        RECEITUÁRIO DE CONTROLE ESPECIAL
                    </Typography>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            paddingLeft: 40,
                            marginTop: 15,
                            marginLeft: 20,
                            marginBottom: 30,
                        }}
                    >
                        {record.recordMedicine.map((medicine) => (
                            <Typography
                                sx={{fontSize: 14, marginTop: 2}}
                            >{`${medicine.medicine.name}: ${medicine.dosage}`}</Typography>
                        ))}
                    </div>
                </div>

                <div style={{flexGrow: 1}}/>
                <div className="page-header">
                    <div className="top-table">
                        <div className="top-table-header">
                            <Typography>IDENTIFICAÇÃO DO COMPRADOR</Typography>
                        </div>
                        <Typography
                            sx={{fontSize: 14, marginLeft: 2}}
                        >{`Nome: `}</Typography>
                        <br/>
                        <div className="top-table-line">
                            <Typography sx={{fontSize: 14}}>{`Ident: `}</Typography>
                            <Typography
                                sx={{fontSize: 14, marginLeft: 13}}
                            >{`Org. Emissor: `}</Typography>
                        </div>
                        <Typography
                            sx={{fontSize: 14, marginLeft: 2}}
                        >{`End: `}</Typography>
                        <br/>
                        <div className="top-table-line">
                            <Typography sx={{fontSize: 14}}>{`Cidade: `}</Typography>
                            <Typography
                                sx={{fontSize: 14, marginLeft: 15}}
                            >{`UF: `}</Typography>
                        </div>
                    </div>
                    <div className="bottom-table">
                        <div className="top-table-header">
                            <Typography>IDENTIFICAÇÃO DO FORNECEDOR</Typography>
                        </div>
                        <div className="top-table-column" style={{marginTop: "40px"}}>
                            <Typography sx={{fontSize: 11, textDecoration: "overline", display: "block"}}>
                                ASSINATURA DO FARMACEUTICO
                            </Typography>
                            <br/>
                            <Typography sx={{fontSize: 11, marginTop: 1, display: "block"}}>
                                DATA: ____/__/___
                            </Typography>
                        </div>
                    </div>
                </div>
                <div className="page-header">
                    <div className="top-table" style={{padding: 10, borderWidth: 0}}>
                        <img src={icpLogo} alt="ICP Brasil" style={{width: 100}}/>
                        <Typography sx={{fontSize: 12, fontWeight: "bold"}}>
                            Receituário de Controle Especial
                        </Typography>
                        <Typography sx={{fontSize: 10}}>
                            Receituário de Controle Especial assinado digitalmente por{" "}
                            {record.appointment.doctor.user.name} em {getCurrentDateTime()},
                            conforme MP n° 2.200-2/2001 e Resolução CFM n° 2.299/2021. <br/>
                            A assinatura digital deste documento poderá ser verificada em
                            https://validar.iti.gov.br. <br/>
                            Acesse o documento em https://web.vivasesaude.com.br/receita/{record.appointment.hash}
                        </Typography>
                    </div>
                    <div className="bottom-table" style={{borderWidth: 0}}>
                        <div className="top-table-line">
                            <QRCode
                                value={`https://web.vivasesaude.com.br/receita/${record.appointment.hash}`}
                                size={100}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {/* Page 1 - Medicines Repeated */}
            <div className="print-outline-top"></div>
            <div
                className="page-one"
                style={{
                    width: "210mm",
                    height: "297mm",
                    display: "flex",
                    flexDirection: "column",
                    padding: "20mm",
                    boxSizing: "border-box",
                }}
            >
                <div style={{display: "flex", flexDirection: "column"}}>
                    <div className="page-header">
                        <div className="top-table">
                            <div className="top-table-header">
                                <Typography>IDENTIFICAÇÃO DO EMITENTE</Typography>
                            </div>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Nome Completo: ${record.appointment.doctor.user.name}`}
                                </Typography>
                            </Box>
                            <div className="top-table-line">
                                <Typography
                                    sx={{fontSize: 14}}
                                >{`CRM: ${record.appointment.doctor.crm}`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`UF: ${record.appointment.doctor.state}`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`N.º: ${record.appointment.doctor.address.substring(
                                    record.appointment.doctor.address.lastIndexOf(",") + 1
                                )}`}</Typography>
                            </div>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Endereço Completo: ${record.appointment.doctor.address}, ${record.appointment.doctor.neighborhood}`}
                                </Typography>
                            </Box>
                            {record.appointment.doctor.commercialPhone && (
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`Telefone: ${record.appointment.doctor.commercialPhone}`}</Typography>
                            )}
                            <div className="top-table-line">
                                <Typography sx={{fontSize: 14}}>{`Cidade: ${
                                    record.appointment.doctor.city[0].toUpperCase() +
                                    record.appointment.doctor.city.substr(1)
                                }`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`UF: ${record.appointment.doctor.state}`}</Typography>
                            </div>
                        </div>
                        <div className="top-table">
                            <div className="top-table-header">
                                <Typography>IDENTIFICAÇÃO DO PACIENTE</Typography>
                            </div>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Nome Completo: ${record.appointment.patient?.user?.name}`}
                                </Typography>
                            </Box>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Endereço completo: ${record.appointment.patient?.address}, ${record.appointment.patient?.neighborhood}`}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{fontSize: 14, marginLeft: 2}}
                            >{`Telefone: ${record.appointment.patient?.user?.phone}`}</Typography>
                            <div className="top-table-line">
                                <Typography sx={{fontSize: 14}}>{`Cidade: ${
                                    record.appointment.patient?.city[0].toUpperCase() +
                                    record.appointment.patient?.city.substring(1)
                                }`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`UF: ${record.appointment.patient?.state}`}</Typography>
                            </div>
                        </div>
                        <div className="via-indicator">
                            <Typography sx={{fontSize: 10, fontWeight: 'bold', whiteSpace: 'nowrap'}}>1ª VIA
                                FARMÁCIA</Typography>
                            <Typography sx={{fontSize: 10, fontWeight: 'bold', whiteSpace: 'nowrap'}}>2ª VIA
                                PACIENTE</Typography>
                        </div>
                    </div>
                    <Typography
                        style={{
                            fontSize: 20,
                            textAlign: "center",
                            marginTop: 20,
                            fontWeight: "bold",
                        }}
                    >
                        RECEITUÁRIO DE CONTROLE ESPECIAL
                    </Typography>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            paddingLeft: 40,
                            marginTop: 15,
                            marginLeft: 20,
                            marginBottom: 30,
                        }}
                    >
                        {record.recordMedicine.map((medicine) => (
                            <Typography
                                sx={{fontSize: 14, marginTop: 2}}
                            >{`${medicine.medicine.name}: ${medicine.dosage}`}</Typography>
                        ))}
                    </div>
                </div>

                <div style={{flexGrow: 1}}/>
                <div className="page-header">
                    <div className="top-table">
                        <div className="top-table-header">
                            <Typography>IDENTIFICAÇÃO DO COMPRADOR</Typography>
                        </div>
                        <Typography
                            sx={{fontSize: 14, marginLeft: 2}}
                        >{`Nome: `}</Typography>
                        <br/>
                        <div className="top-table-line">
                            <Typography sx={{fontSize: 14}}>{`Ident: `}</Typography>
                            <Typography
                                sx={{fontSize: 14, marginLeft: 13}}
                            >{`Org. Emissor: `}</Typography>
                        </div>
                        <Typography
                            sx={{fontSize: 14, marginLeft: 2}}
                        >{`End: `}</Typography>
                        <br/>
                        <div className="top-table-line">
                            <Typography sx={{fontSize: 14}}>{`Cidade: `}</Typography>
                            <Typography
                                sx={{fontSize: 14, marginLeft: 15}}
                            >{`UF: `}</Typography>
                        </div>
                    </div>
                    <div className="bottom-table">
                        <div className="top-table-header">
                            <Typography>IDENTIFICAÇÃO DO FORNECEDOR</Typography>
                        </div>
                        <div className="top-table-column" style={{marginTop: "40px"}}>
                            <Typography sx={{fontSize: 11, textDecoration: "overline", display: "block"}}>
                                ASSINATURA DO FARMACEUTICO
                            </Typography>
                            <br/>
                            <Typography sx={{fontSize: 11, marginTop: 1, display: "block"}}>
                                DATA: ____/__/___
                            </Typography>
                        </div>
                    </div>
                </div>
                <div className="page-header">
                    <div className="top-table" style={{padding: 10, borderWidth: 0}}>
                        <img src={icpLogo} alt="ICP Brasil" style={{width: 100}}/>
                        <Typography sx={{fontSize: 12, fontWeight: "bold"}}>
                            Receituário de Controle Especial
                        </Typography>
                        <Typography sx={{fontSize: 10}}>
                            Receituário de Controle Especial assinado digitalmente por{" "}
                            {record.appointment.doctor.user.name} em {getCurrentDateTime()},
                            conforme MP n° 2.200-2/2001 e Resolução CFM n° 2.299/2021. <br/>
                            A assinatura digital deste documento poderá ser verificada em
                            https://validar.iti.gov.br. <br/>
                            Acesse o documento em https://web.vivasesaude.com.br/receita/{record.appointment.hash}
                        </Typography>
                    </div>
                    <div className="bottom-table" style={{borderWidth: 0}}>
                        <div className="top-table-line">
                            <QRCode
                                value={`https://web.vivasesaude.com.br/receita/${record.appointment.hash}`}
                                size={100}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Page 2 - Exams */}
            {laboratoryExamsRequested.length > 0 && (
                <>
                    <div className="print-outline-top"></div>
                    <div
                        className="page-one"
                        style={{
                            width: "210mm",
                            height: "297mm",
                            display: "flex",
                            flexDirection: "column",
                            padding: "20mm",
                            boxSizing: "border-box",
                        }}
                    >
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            {/* Doctor & Patient info */}
                            <div className="page-header">
                                <div className="top-table">
                                    <div className="top-table-header">
                                        <Typography>IDENTIFICAÇÃO DO EMITENTE</Typography>
                                    </div>
                                    <Box sx={{ width: "100%", px: 2, boxSizing: "border-box" }}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {`Nome Completo: ${record.appointment.doctor.user.name}`}
                                        </Typography>
                                    </Box>
                                    <div className="top-table-line">
                                        <Typography sx={{ fontSize: 14 }}>
                                            {`CRM: ${record.appointment.doctor.crm}`}
                                        </Typography>
                                        <Typography sx={{ fontSize: 14, marginLeft: 2 }}>
                                            {`UF: ${record.appointment.doctor.state}`}
                                        </Typography>
                                        <Typography sx={{ fontSize: 14, marginLeft: 2 }}>
                                            {`N.º: ${record.appointment.doctor.address.substring(
                                                record.appointment.doctor.address.lastIndexOf(",") + 1
                                            )}`}
                                        </Typography>
                                    </div>
                                    <Box sx={{ width: "100%", px: 2, boxSizing: "border-box" }}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {`Endereço Completo: ${record.appointment.doctor.address}, ${record.appointment.doctor.neighborhood}`}
                                        </Typography>
                                    </Box>
                                    {record.appointment.doctor.commercialPhone && (
                                        <Typography sx={{ fontSize: 14, marginLeft: 2 }}>
                                            {`Telefone: ${record.appointment.doctor.commercialPhone}`}
                                        </Typography>
                                    )}
                                    <div className="top-table-line">
                                        <Typography sx={{ fontSize: 14 }}>
                                            {`Cidade: ${
                                                record.appointment.doctor.city[0].toUpperCase() +
                                                record.appointment.doctor.city.substr(1)
                                            }`}
                                        </Typography>
                                        <Typography sx={{ fontSize: 14, marginLeft: 2 }}>
                                            {`UF: ${record.appointment.doctor.state}`}
                                        </Typography>
                                    </div>
                                </div>

                                <div className="top-table">
                                    <div className="top-table-header">
                                        <Typography>IDENTIFICAÇÃO DO PACIENTE</Typography>
                                    </div>
                                    <Box sx={{ width: "100%", px: 2, boxSizing: "border-box" }}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {`Nome Completo: ${record.appointment.patient?.user?.name}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ width: "100%", px: 2, boxSizing: "border-box" }}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {`Endereço completo: ${record.appointment.patient?.address}, ${record.appointment.patient?.neighborhood}`}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 14, marginLeft: 2 }}>
                                        {`Telefone: ${record.appointment.patient?.user?.phone}`}
                                    </Typography>
                                    <div className="top-table-line">
                                        <Typography sx={{ fontSize: 14 }}>
                                            {`Cidade: ${
                                                record.appointment.patient?.city[0].toUpperCase() +
                                                record.appointment.patient?.city.substring(1)
                                            }`}
                                        </Typography>
                                        <Typography sx={{ fontSize: 14, marginLeft: 2 }}>
                                            {`UF: ${record.appointment.patient?.state}`}
                                        </Typography>
                                    </div>
                                </div>
                            </div>

                            {/* Title */}
                            <Typography
                                style={{
                                    fontSize: 20,
                                    textAlign: "center",
                                    marginTop: 20,
                                    fontWeight: "bold",
                                }}
                            >
                                Exames Solicitados
                            </Typography>

                            {/* Exams list */}
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    paddingLeft: 40,
                                    marginTop: 15,
                                    marginLeft: 20,
                                    marginBottom: 30,
                                }}
                            >
                                {laboratoryExamsRequested
                                    .filter((exam) => exam?.trim() !== "")
                                    .map((exam, idx) => (
                                        <Typography key={idx} sx={{ fontSize: 14, marginTop: 2 }}>
                                            {exam}
                                        </Typography>
                                    ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ flexGrow: 1 }} />
                        <div className="page-header">
                            <div className="top-table" style={{ padding: 10, borderWidth: 0 }}>
                                <img src={icpLogo} alt="ICP Brasil" style={{ width: 100 }} />
                                <Typography sx={{ fontSize: 12, fontWeight: "bold" }}>
                                    Receituário de Controle Especial
                                </Typography>
                                <Typography sx={{ fontSize: 10 }}>
                                    Receituário de Controle Especial assinado digitalmente por{" "}
                                    {record.appointment.doctor.user.name} em {getCurrentDateTime()},
                                    conforme MP n° 2.200-2/2001 e Resolução CFM n° 2.299/2021. <br />
                                    A assinatura digital deste documento poderá ser verificada em{" "}
                                    https://validar.iti.gov.br. <br />
                                    Acesse o documento em
                                    https://web.vivasesaude.com.br/receita/{record.appointment.hash}
                                </Typography>
                            </div>
                            <div className="bottom-table" style={{ borderWidth: 0 }}>
                                <div className="top-table-line">
                                    <QRCode
                                        value={`https://web.vivasesaude.com.br/receita/${record.appointment.hash}`}
                                        size={100}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Page 3 - Image Exams */}
            {examsRequested.length > 0 &&
                examsRequested
                    .filter((exam) => exam?.trim() !== "")
                    .map((exam) => (
                        <>
                            <div className="print-outline-top"></div>
                            <div
                                className="page-one"
                                style={{
                                    width: "210mm",
                                    height: "297mm",
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: "20mm",
                                    boxSizing: "border-box",
                                }}
                            >
                                <div style={{display: "flex", flexDirection: "column"}}>
                                    <div className="page-header">
                                        <div className="top-table">
                                            <div className="top-table-header">
                                                <Typography>IDENTIFICAÇÃO DO EMITENTE</Typography>
                                            </div>
                                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                                <Typography
                                                    component="div"
                                                    sx={{
                                                        fontSize: 14,
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {`Nome Completo: ${record.appointment.doctor.user.name}`}
                                                </Typography>
                                            </Box>
                                            <div className="top-table-line">
                                                <Typography
                                                    sx={{fontSize: 14}}
                                                >{`CRM: ${record.appointment.doctor.crm}`}</Typography>
                                                <Typography
                                                    sx={{fontSize: 14, marginLeft: 2}}
                                                >{`UF: ${record.appointment.doctor.state}`}</Typography>
                                                <Typography
                                                    sx={{fontSize: 14, marginLeft: 2}}
                                                >{`N.º: ${record.appointment.doctor.address.substring(
                                                    record.appointment.doctor.address.lastIndexOf(",") + 1
                                                )}`}</Typography>
                                            </div>
                                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                                <Typography
                                                    component="div"
                                                    sx={{
                                                        fontSize: 14,
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {`Endereço Completo: ${record.appointment.doctor.address}, ${record.appointment.doctor.neighborhood}`}
                                                </Typography>
                                            </Box>
                                            {record.appointment.doctor.commercialPhone && (
                                                <Typography
                                                    sx={{fontSize: 14, marginLeft: 2}}
                                                >{`Telefone: ${record.appointment.doctor.commercialPhone}`}</Typography>
                                            )}
                                            <div className="top-table-line">
                                                <Typography sx={{fontSize: 14}}>{`Cidade: ${
                                                    record.appointment.doctor.city[0].toUpperCase() +
                                                    record.appointment.doctor.city.substr(1)
                                                }`}</Typography>
                                                <Typography
                                                    sx={{fontSize: 14, marginLeft: 2}}
                                                >{`UF: ${record.appointment.doctor.state}`}</Typography>
                                            </div>
                                        </div>
                                        <div className="top-table">
                                            <div className="top-table-header">
                                                <Typography>IDENTIFICAÇÃO DO PACIENTE</Typography>
                                            </div>
                                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                                <Typography
                                                    component="div"
                                                    sx={{
                                                        fontSize: 14,
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {`Nome Completo: ${record.appointment.patient?.user?.name}`}
                                                </Typography>
                                            </Box>
                                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                                <Typography
                                                    component="div"
                                                    sx={{
                                                        fontSize: 14,
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    {`Endereço completo: ${record.appointment.patient?.address}, ${record.appointment.patient?.neighborhood}`}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{fontSize: 14, marginLeft: 2}}
                                            >{`Telefone: ${record.appointment.patient?.user?.phone}`}</Typography>
                                            <div className="top-table-line">
                                                <Typography sx={{fontSize: 14}}>{`Cidade: ${
                                                    record.appointment.patient?.city[0].toUpperCase() +
                                                    record.appointment.patient?.city.substring(1)
                                                }`}</Typography>
                                                <Typography
                                                    sx={{fontSize: 14, marginLeft: 2}}
                                                >{`UF: ${record.appointment.patient?.state}`}</Typography>
                                            </div>
                                        </div>
                                    </div>
                                    <Typography
                                        style={{
                                            fontSize: 20,
                                            textAlign: "center",
                                            marginTop: 20,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Exames Solicitados
                                    </Typography>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            paddingLeft: 40,
                                            marginTop: 15,
                                            marginLeft: 20,
                                            marginBottom: 30,
                                        }}
                                    >
                                        <Typography
                                            sx={{fontSize: 14, marginTop: 2}}
                                        >{`${exam}`}</Typography>
                                    </div>
                                </div>

                                <div style={{flexGrow: 1}}/>
                                <div className="page-header">
                                    <div
                                        className="top-table"
                                        style={{padding: 10, borderWidth: 0}}
                                    >
                                        <img src={icpLogo} alt="ICP Brasil" style={{width: 100}}/>
                                        <Typography sx={{fontSize: 12, fontWeight: "bold"}}>
                                            Receituário de Controle Especial
                                        </Typography>
                                        <Typography sx={{fontSize: 10}}>
                                            Receituário de Controle Especial assinado digitalmente por{" "}
                                            {record.appointment.doctor.user.name} em{" "}
                                            {getCurrentDateTime()}, conforme MP n° 2.200-2/2001 e
                                            Resolução CFM n° 2.299/2021. <br/>
                                            A assinatura digital deste documento poderá ser verificada
                                            em https://validar.iti.gov.br. <br/>
                                            Acesse o documento em
                                            https://web.vivasesaude.com.br/receita/{record.appointment.hash}
                                        </Typography>
                                    </div>
                                    <div className="bottom-table" style={{borderWidth: 0}}>
                                        <div className="top-table-line">
                                            <QRCode
                                                value={`https://web.vivasesaude.com.br/receita/${record.appointment.hash}`}
                                                size={100}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )
                )
            }

            {/* Page 4 - Receipt */}
            <div className="print-outline-top"></div>
            <div
                className="page-one"
                style={{
                    width: "210mm",
                    height: "297mm",
                    display: "flex",
                    flexDirection: "column",
                    padding: "20mm",
                    boxSizing: "border-box",
                }}
            >
                <div style={{display: "flex", flexDirection: "column"}}>
                    <div className="page-header">
                        <div className="top-table">
                            <div className="top-table-header">
                                <Typography>IDENTIFICAÇÃO DO EMITENTE</Typography>
                            </div>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Nome Completo: ${record.appointment.doctor.user.name}`}
                                </Typography>
                            </Box>
                            <div className="top-table-line">
                                <Typography
                                    sx={{fontSize: 14}}
                                >{`CRM: ${record.appointment.doctor.crm}`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`UF: ${record.appointment.doctor.state}`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`N.º: ${record.appointment.doctor.address.substring(
                                    record.appointment.doctor.address.lastIndexOf(",") + 1
                                )}`}</Typography>
                            </div>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Endereço Completo: ${record.appointment.doctor.address}, ${record.appointment.doctor.neighborhood}`}
                                </Typography>
                            </Box>
                            {record.appointment.doctor.commercialPhone && (
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`Telefone: ${record.appointment.doctor.commercialPhone}`}</Typography>
                            )}
                            <div className="top-table-line">
                                <Typography sx={{fontSize: 14}}>{`Cidade: ${
                                    record.appointment.doctor.city[0].toUpperCase() +
                                    record.appointment.doctor.city.substr(1)
                                }`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`UF: ${record.appointment.doctor.state}`}</Typography>
                            </div>
                        </div>
                        <div className="top-table">
                            <div className="top-table-header">
                                <Typography>IDENTIFICAÇÃO DO PACIENTE</Typography>
                            </div>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Nome Completo: ${record.appointment.patient?.user?.name}`}
                                </Typography>
                            </Box>
                            <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: 14,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {`Endereço completo: ${record.appointment.patient?.address}, ${record.appointment.patient?.neighborhood}`}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{fontSize: 14, marginLeft: 2}}
                            >{`Telefone: ${record.appointment.patient?.user?.phone}`}</Typography>
                            <div className="top-table-line">
                                <Typography sx={{fontSize: 14}}>{`Cidade: ${
                                    record.appointment.patient?.city[0].toUpperCase() +
                                    record.appointment.patient?.city.substring(1)
                                }`}</Typography>
                                <Typography
                                    sx={{fontSize: 14, marginLeft: 2}}
                                >{`UF: ${record.appointment.patient?.state}`}</Typography>
                            </div>
                        </div>
                    </div>
                    <Typography
                        style={{
                            fontSize: 20,
                            textAlign: "center",
                            marginTop: 20,
                            fontWeight: "bold",
                        }}
                    >
                        Recibo de Pagamento
                    </Typography>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            paddingLeft: 40,
                            marginTop: 15,
                            marginLeft: 20,
                            marginBottom: 30,
                        }}
                    >
                        <Typography sx={{fontSize: 14, marginTop: 2}}>
                            Recebi do(a) paciente acima identificado(a) a quantia de {" "}
                            {Intl.NumberFormat("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                            }).format(record.appointment.doctor.appointmentValue)}
                            {" "} referente à consulta realizada em{" "}
                            {new Intl.DateTimeFormat("pt-BR").format(
                                new Date(record.appointment.appointment_end_time)
                            )}
                            .
                        </Typography>
                    </div>
                </div>

                <div style={{flexGrow: 1}}/>
                <div className="page-header">
                    <div className="top-table" style={{padding: 10, borderWidth: 0}}>
                        <img src={icpLogo} alt="ICP Brasil" style={{width: 100}}/>
                        <Typography sx={{fontSize: 12, fontWeight: "bold"}}>
                            Receituário de Controle Especial
                        </Typography>
                        <Typography sx={{fontSize: 10}}>
                            Receituário de Controle Especial assinado digitalmente por{" "}
                            {record.appointment.doctor.user.name} em {getCurrentDateTime()},
                            conforme MP n° 2.200-2/2001 e Resolução CFM n° 2.299/2021. <br/>
                            A assinatura digital deste documento poderá ser verificada em
                            https://validar.iti.gov.br. <br/>
                            Acesse o documento em https://web.vivasesaude.com.br/receita/{record.appointment.hash}
                        </Typography>
                    </div>
                    <div className="bottom-table" style={{borderWidth: 0}}>
                        <div className="top-table-line">
                            <QRCode
                                value={`https://web.vivasesaude.com.br/receita/${record.appointment.hash}`}
                                size={100}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Page 5 - Health certificate */}
            {healthCertificate && healthCertificate.trim() !== "" && (
                <>
                    <div className="print-outline-top"></div>
                    <div
                        className="page-one"
                        style={{
                            width: "210mm",
                            height: "297mm",
                            display: "flex",
                            flexDirection: "column",
                            padding: "20mm",
                            boxSizing: "border-box",
                        }}
                    >
                        <div style={{display: "flex", flexDirection: "column"}}>
                            <div className="page-header">
                                <div className="top-table">
                                    <div className="top-table-header">
                                        <Typography>IDENTIFICAÇÃO DO EMITENTE</Typography>
                                    </div>
                                    <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {`Nome Completo: ${record.appointment.doctor.user.name}`}
                                        </Typography>
                                    </Box>
                                    <div className="top-table-line">
                                        <Typography
                                            sx={{fontSize: 14}}
                                        >{`CRM: ${record.appointment.doctor.crm}`}</Typography>
                                        <Typography
                                            sx={{fontSize: 14, marginLeft: 2}}
                                        >{`UF: ${record.appointment.doctor.state}`}</Typography>
                                        <Typography
                                            sx={{fontSize: 14, marginLeft: 2}}
                                        >{`N.º: ${record.appointment.doctor.address.substring(
                                            record.appointment.doctor.address.lastIndexOf(",") + 1
                                        )}`}</Typography>
                                    </div>
                                    <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {`Endereço Completo: ${record.appointment.doctor.address}, ${record.appointment.doctor.neighborhood}`}
                                        </Typography>
                                    </Box>
                                    {record.appointment.doctor.commercialPhone && (
                                        <Typography
                                            sx={{fontSize: 14, marginLeft: 2}}
                                        >{`Telefone: ${record.appointment.doctor.commercialPhone}`}</Typography>
                                    )}
                                    <div className="top-table-line">
                                        <Typography
                                            sx={{fontSize: 14}}>{`Cidade: ${record.appointment.doctor.city[0].toUpperCase() +
                                        record.appointment.doctor.city.substr(1)}`}</Typography>
                                        <Typography
                                            sx={{fontSize: 14, marginLeft: 2}}
                                        >{`UF: ${record.appointment.doctor.state}`}</Typography>
                                    </div>
                                </div>
                                <div className="top-table">
                                    <div className="top-table-header">
                                        <Typography>IDENTIFICAÇÃO DO PACIENTE</Typography>
                                    </div>
                                    <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {`Nome Completo: ${record.appointment.patient?.user?.name}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{width: '100%', px: 2, boxSizing: 'border-box'}}>
                                        <Typography
                                            component="div"
                                            sx={{
                                                fontSize: 14,
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {`Endereço completo: ${record.appointment.patient?.address}, ${record.appointment.patient?.neighborhood}`}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{fontSize: 14, marginLeft: 2}}
                                    >{`Telefone: ${record.appointment.patient?.user?.phone}`}</Typography>
                                    <div className="top-table-line">
                                        <Typography
                                            sx={{fontSize: 14}}>{`Cidade: ${record.appointment.patient?.city[0].toUpperCase() +
                                        record.appointment.patient?.city.substring(1)}`}</Typography>
                                        <Typography
                                            sx={{fontSize: 14, marginLeft: 2}}
                                        >{`UF: ${record.appointment.patient?.state}`}</Typography>
                                    </div>
                                </div>
                            </div>
                            <Typography
                                style={{
                                    fontSize: 20,
                                    textAlign: "center",
                                    marginTop: 20,
                                    fontWeight: "bold",
                                }}
                            >
                                Atestado
                            </Typography>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    paddingLeft: 40,
                                    marginTop: 15,
                                    marginLeft: 20,
                                    marginBottom: 30,
                                }}
                            >
                                {showCid && (
                                    <Typography sx={{fontSize: 14, marginTop: 2}}>
                                        CID: {record.cid}
                                    </Typography>
                                )}
                                <Typography sx={{fontSize: 14, marginTop: 2}}>
                                    {healthCertificate}
                                </Typography>
                            </div>
                        </div>

                        <div style={{flexGrow: 1}}/>
                        <div className="page-header">
                            <div className="top-table" style={{padding: 10, borderWidth: 0}}>
                                <img src={icpLogo} alt="ICP Brasil" style={{width: 100}}/>
                                <Typography sx={{fontSize: 12, fontWeight: "bold"}}>
                                    Receituário de Controle Especial
                                </Typography>
                                <Typography sx={{fontSize: 10}}>
                                    Receituário de Controle Especial assinado digitalmente por{" "}
                                    {record.appointment.doctor.user.name} em {getCurrentDateTime()},
                                    conforme MP n° 2.200-2/2001 e Resolução CFM n° 2.299/2021. <br/>
                                    A assinatura digital deste documento poderá ser verificada em
                                    https://validar.iti.gov.br. <br/>
                                    Acesse o documento em
                                    https://web.vivasesaude.com.br/receita/{record.appointment.hash}
                                </Typography>
                            </div>
                            <div className="bottom-table" style={{borderWidth: 0}}>
                                <div className="top-table-line">
                                    <QRCode
                                        value={`https://web.vivasesaude.com.br/receita/${record.appointment.hash}`}
                                        size={100}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default ReceiptPDF;
